import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faTrashAlt,
  faSliders,
  faRedoAlt,
  faShareSquare
} from "@fortawesome/free-solid-svg-icons";

import InvitedUsers from "../../components/Registration/InvitedUsers";
import RegisteredUsers from "../../components/Registration/RegisteredUsers";
import AllUsers from "../../components/Registration/AllUsers";
import AddAppUser from "./AddAppUser";

import { getCookie } from "../../utils/cookieUtil";
import {
  confirmDeleteInvitedUser,
  confirmDeleteRegisteredUser,
} from "../../services/authService";

// Spinner Component
const LoadingSpinner = () => (
  <div className="fixed inset-0 flex h-full items-center justify-center bg-gray-800 bg-opacity-50 z-50">
    <div className="w-16 h-16 border-t-4 border-[#900028] border-solid rounded-full animate-spin"></div>
  </div>
);

export default function UserManagement() {
  const [activeTab, setActiveTab] = useState("all");
  const [counter, setCounter] = useState(0);
  const [permission, setPermission] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState({ invited: [], registered: [] });
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [reloadCounter, setReloadCounter] = useState(0);
  const [loading, setLoading] = useState(false);
  // const [reload, setReload] = useState(false);

  const dropdownRef = useRef();
  const navigate = useNavigate();

  const exportRef = useRef();

  useEffect(() => {
    const roles = getCookie("roleId");
    if (roles) {
      setPermission(roles);
    }
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowGroupDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // const handleSearch = () => {
  //   // Implement search logic here if needed
  // };

  const handleReload = () => {
    setReloadCounter((prev) => prev + 1);
  };

  const handleGlobalShare = () => {
    if (exportRef.current) {
      exportRef.current(); // Trigger the export function inside AdminUsersTable
    }
  };

  const handleFilter = () => {
    setShowGroupDropdown((prev) => !prev);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);

      for (const id of selectedIds.invited ?? []) {
        await confirmDeleteInvitedUser(id);
      }

      for (const id of selectedIds.registered ?? []) {
        await confirmDeleteRegisteredUser(id);
      }

      setSelectedIds({ invited: [], registered: [] });
      setCounter((prev) => prev + 1);
      setReloadCounter((prev) => prev + 1);
      setShowConfirmDelete(false);
    } catch (error) {
      console.error("Error deleting users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGroupSelect = (group) => {
    setShowGroupDropdown(false);
    setSelectedGroup(group);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  // Calculate if delete button should be disabled
  const isDeleteDisabled =
    (!selectedIds.invited || selectedIds.invited.length === 0) &&
    (!selectedIds.registered || selectedIds.registered.length === 0);

  return (
    <div className="p-4 space-y-4 relative">
      {/* Search and Action Buttons */}
      <div className="flex flex-col sm:flex-row items-center mb-6 space-y-2 sm:space-y-0 sm:space-x-2 justify-end">
        <div className="flex items-center w-full max-w-xs border border-[#900028] rounded-md focus-within:border-[#900028]">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' }
            className="flex-grow p-2 sm:p-1 outline-none rounded-l-md border-none text-sm sm:text-base"
            placeholder="Search by mail"
          />
          <button
            className="bg-[#900028] text-white px-3 sm:px-4 py-2 sm:py-1 rounded-r-md"
            aria-label="Search"
          >
            <FontAwesomeIcon icon={faSearch} className="text-base sm:text-lg" />
          </button>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3 items-center">
             <button
            onClick={() => setShowConfirmDelete(true)}
            disabled={isDeleteDisabled}
            style={{
              cursor: isDeleteDisabled ? "not-allowed" : "pointer",
              background: "transparent",
              border: "none",
            }}
            className="p-1 sm:p-2"
            aria-label="Delete Selected Users"
          >
            <FontAwesomeIcon
              icon={faTrashAlt}
              className={`text-xl sm:text-2xl md:text-3xl text-[#900028] ${isDeleteDisabled ? "opacity-50" : "opacity-100"}`}
            />
          </button>


 {/* Filter Button with Dropdown */}
          <div className="relative" ref={dropdownRef}>
             <button onClick={handleFilter} className="p-2 focus:outline-none">
              <FontAwesomeIcon
                icon={faSliders}
                style={{ color: "#900028", fontSize: "25px" }}
              />
            </button>

            {showGroupDropdown && (
              <div className="absolute right-0 mt-2 w-40 bg-gray-100 rounded shadow-lg z-50">
                {["All Groups", "A", "B", "C"].map((group) => (
                  <button
                    key={group}
                    onClick={() => handleGroupSelect(group)}
                    className="w-full text-left px-4 py-2 hover:bg-[#900028] hover:text-black"
                  >
                    {group}
                  </button>
                ))}
              </div>
            )}
          </div>






          <button
            onClick={handleGlobalShare}
            className="p-1 sm:p-2"
            aria-label="Share selected users"
          >
            <FontAwesomeIcon
              icon={faShareSquare}
              className="text-xl sm:text-2xl text-[#900028]"
            />
          </button>

          <button
            onClick={handleReload}
            className="p-1 sm:p-2"
            aria-label="Reload data"
          >
            <FontAwesomeIcon
              icon={faRedoAlt}
              className="text-xl sm:text-2xl text-[#900028]"
            />
          </button>
        </div>
      </div>

      {/* Tabs and Invite Button */}
      <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-2">
        <div className="flex rounded-full overflow-hidden border">
          {["all", "registered", "invited"].map((tabKey, idx) => (
            <button
              key={tabKey}
              className={`px-3 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm font-semibold transition-colors duration-200 border
                ${activeTab === tabKey
                  ? "bg-[#900028] text-white border-transparent"
                  : "bg-gradient-to-b from-gray-100 to-gray-300 text-black hover:border-[#900028]"
                }
                ${idx === 0 ? "rounded-l-full border-r" : ""}
                ${idx === 1 ? "border-r" : ""}
                ${idx === 2 ? "rounded-r-full" : ""}`}
              onClick={() => {
                setActiveTab(tabKey);
                setCounter((prev) => prev + 1);
                setSearchQuery("");
                setSelectedGroup("");
              }}
              aria-current={activeTab === tabKey ? "page" : undefined}
            >
              {tabKey === "all"
                ? "All Users"
                : tabKey === "registered"
                  ? "Registered Users"
                  : "Invited Users"}
            </button>
          ))}
        </div>

        <button
          className="px-3 sm:px-5 py-1 sm:py-2 text-xs sm:text-sm font-semibold transition-colors duration-200 
            bg-gradient-to-b from-gray-100 to-gray-300 text-black rounded-full border border-gray-900 hover:border-[#900028]"
          onClick={() => setShowModal(true)}
          aria-label="Invite Participants"
        >
          Invite Participants
        </button>
      </div>

      <div className="relative">
        {/* AddAppUser Modal */}
        {showModal && (
          <div className="fixed bg-black bg-opacity-30 z-40 inset-0 flex justify-center items-center p-4">
            <div className="p-6 w-full max-w-3xl ">
              <AddAppUser closeModal={handleClose} onSuccess={handleReload} />
            </div>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === "registered" ? (
          <RegisteredUsers
            counter={reloadCounter}
            searchMailQuery={searchQuery}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            selectedGroup={selectedGroup}
            // reload={reload}
          />
        ) : activeTab === "invited" ? (
          <InvitedUsers
            counter={reloadCounter}
            searchMailQuery={searchQuery}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            selectedGroup={selectedGroup}
          //  reload={reload}
            onExportRef={exportRef}
          />
        ) : (
          <AllUsers
            counter={reloadCounter}
            searchMailQuery={searchQuery}
            selectedGroup={selectedGroup}
            onExportRef={exportRef}
            //  reload={reload}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showConfirmDelete && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center px-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
              <h3 className="text-xl font-semibold mb-4">Confirm Delete</h3>
              <p className="mb-6">
                Are you sure you want to delete the selected users?
              </p>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowConfirmDelete(false)}
                  className="px-4 py-2 rounded bg-gray-300 text-black hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 flex items-center gap-2"
                  disabled={loading}
                >
                  Delete
                  {loading && <LoadingSpinner />}
                </button>
              </div>
            </div>
          </div>
        )}

      
      </div>
    </div>
  );
}









